import { getSelectedExhibitionId, getSelfRegistration } from '@app/app.reducer';
import { FieldKey, OptionKey } from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { UploadedFiles } from '@core/data-access/models/file-upload.model';
import { CheckboxFormField, FileUploadFormField } from '@core/data-access/models/form/form-store.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { Person } from '@core/data-access/models/person.model';
import { QuestionnaireApiService } from '@core/data-access/services/api/questionnaire-api.service';
import { TicketHolderQuestionnaireDynamicFields } from '@core/features/ticket-assignment/models/ticket-assignment-store.model';
import { selectTicketHolderQuestionnaireDynamicFieldsByTicketPersonId } from '@core/features/ticket-assignment/store/ticket-assignment.selectors';
import { setSyncForms } from '@core/utils/sync-forms';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { TariffUuid } from '@products/models/tariff.model';
import { selectAllTicketHolderTariffUuidsWithTicketPersonId } from '@store/products/booking/tariff/booking-tariff.selectors';
import { at, isEmpty } from 'lodash';
import {
  catchError,
  concatMap,
  debounceTime,
  first,
  from,
  iif,
  map,
  mergeMap,
  of,
  OperatorFunction,
  pipe,
  reduce,
  switchMap,
  withLatestFrom
} from 'rxjs';

export const syncGDPRConsentAndCVDocumentFormFields = (
  isGDPRConsentChecked: boolean,
  person: Person,
  value: UploadedFiles
) => {
  const syncedCVDocumentFormFields = syncCVDocumentFormFields(isGDPRConsentChecked, person, value);

  const updatedGDPRConsentCheckboxFormField: Partial<Record<FieldKey, CheckboxFormField>> = {
    [FieldKey.GDPR]: {
      value: {
        [OptionKey.GDPRConsent]: {
          value: isGDPRConsentChecked,
          isQuestionnaire: false
        }
      },
      isQuestionnaire: false
    }
  };

  return {
    ...syncedCVDocumentFormFields,
    formFields: {
      ...updatedGDPRConsentCheckboxFormField,
      ...syncedCVDocumentFormFields.formFields
    }
  };
};

export const syncCVDocumentFormFields = (isGDPRConsentChecked: boolean, person: Person, value: UploadedFiles) => {
  const syncForms = setSyncForms(person, [FormName.TicketHolder]);

  const updatedCVFileUploadFormField: Partial<Record<FieldKey, FileUploadFormField>> = {
    [FieldKey.CV]: {
      value,
      isQuestionnaire: false,
      isDisabled: !isGDPRConsentChecked
    }
  };

  return {
    person,
    originForm: FormName.TicketHolder,
    syncForms,
    formFields: updatedCVFileUploadFormField
  };
};

export const getTicketHolderQuestionnairePipe = (
  store: Store,
  ticketAssignmentDebounceTime: number,
  questionnaireApiService: QuestionnaireApiService
): OperatorFunction<
  { tariffUuids: TariffUuid[] },
  {
    tariffUuids: TariffUuid[];
    ticketHolderQuestionnaireDynamicFields: TicketHolderQuestionnaireDynamicFields;
  }
> =>
  pipe(
    debounceTime(ticketAssignmentDebounceTime),
    concatLatestFrom(() => store.select(selectAllTicketHolderTariffUuidsWithTicketPersonId)),
    map(([{ tariffUuids }, allTicketHolderTariffUuidsWithTicketPersonId]) => ({
      tariffUuids,
      uniqueTicketPersonIds: new Set(at(allTicketHolderTariffUuidsWithTicketPersonId, tariffUuids))
    })),
    concatMap(({ tariffUuids, uniqueTicketPersonIds }) =>
      from(uniqueTicketPersonIds).pipe(
        mergeMap((uniqueTicketPersonId) =>
          store.pipe(
            select(selectTicketHolderQuestionnaireDynamicFieldsByTicketPersonId(uniqueTicketPersonId)),
            first(),
            withLatestFrom(store.select(getSelectedExhibitionId), store.select(getSelfRegistration)),
            switchMap(([dynamicQuestionnaireFields, selectedExhibitionId, isSelfRegistration]) =>
              iif(
                () => !isEmpty(dynamicQuestionnaireFields),
                of(dynamicQuestionnaireFields),
                questionnaireApiService.getTicketHolderQuestionnaire$(
                  [uniqueTicketPersonId],
                  selectedExhibitionId,
                  isSelfRegistration
                )
              )
            ),
            catchError(() => of([])),
            map((dynamicQuestionnaireFields) => ({
              tariffUuids,
              uniqueTicketPersonId,
              dynamicQuestionnaireFields
            }))
          )
        ),
        reduce((ticketHolderQuestionnaireDynamicFields, { uniqueTicketPersonId, dynamicQuestionnaireFields }) => {
          ticketHolderQuestionnaireDynamicFields[uniqueTicketPersonId] = dynamicQuestionnaireFields;

          return ticketHolderQuestionnaireDynamicFields;
        }, {} as TicketHolderQuestionnaireDynamicFields),
        map((ticketHolderQuestionnaireDynamicFields) => ({ tariffUuids, ticketHolderQuestionnaireDynamicFields }))
      )
    )
  );
